import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { logger } from '@/shared/lib/logger';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';
import { createFiltersetService, Filterset } from '@/app/services/filtersetService';
import { generateFilterExpression, getInvalidStoredFilterKeys, parseFilterExpression, sanitizeFilterExpression, sanitizeQueryParamsForNavigation, sanitizeUrlFilterExpression, toStoredFilterExpression } from '../utils/filterUtils';
import { useAuth } from '@/shared/providers/AuthProvider';
import { resolveFilterTargetModel } from '@/shared/constants/filterTargetModels';
import { hasFilterQueryParams, sanitizeFilterQueryParams } from '@/shared/filters/filterDefaultOverrides';

/** Thrown by saveFilterset when the chosen name already exists on the server. */
export class FiltersetNameExistsError extends Error {
    constructor() {
        super('Filterset name already exists');
        this.name = 'FiltersetNameExistsError';
    }
}

/**
 * Configuration for filter management
 */
export interface FilterManagementConfig<T extends Record<string, any>> {
    /**
     * Target model for saving/loading filtersets (e.g., 'selfservice_user', 'vehicle', 'client')
     */
    targetModel: string;

    /**
     * Default filter values
     */
    defaultFilters: T;

    /**
     * Optional defaults applied only once on first load when no meaningful q.* params exist.
     * After that, URL becomes the source of truth and resetFilters still uses defaultFilters.
     */
    initialDefaultsOnFirstLoad?: Partial<T>;

    /**
     * Optional: Custom filter to params transformer
     * By default uses generateFilterExpression from filterUtils
     */
    filterToParams?: (filters: T) => string;

    /**
     * Optional: Custom params to filter transformer
     * By default uses parseFilterExpression from filterUtils
     */
    paramsToFilter?: (params: URLSearchParams) => Partial<T>;

    /**
     * Optional: Custom transformer for the expression stored in k3_user_filter.expression.
     * Use this when saved-filter key/value rules differ from the page URL contract.
     */
    filtersToSavedFilterExpression?: (filters: T) => string;

    /**
     * Optional: Custom parser for expressions loaded from k3_user_filter.expression.
     * Use this alongside filtersToSavedFilterExpression for page-specific saved-filter mappings.
     */
    savedFilterExpressionToFilters?: (params: URLSearchParams) => Partial<T>;
}

/**
 * Return type for useFilterManagement hook
 */
export interface FilterManagementReturn<T extends Record<string, any>> {
    // Current filter state
    filters: T;
    appliedFilters: T;

    // Filter operations
    setFilter: (key: keyof T, value: any) => void;
    setFilters: (filters: Partial<T>) => void;
    resetFilters: () => void;
    applyFilters: (extraParams?: Record<string, string>) => void;

    // Saved filterset operations
    saveFilterset: (name: string, isShared: boolean) => Promise<void>;
    searchSavedFilterNames: (searchTerm: string) => Promise<Filterset[]>;
    updateFilterset: (id: number, data: Partial<Filterset>) => Promise<void>;
    applySavedFilterset: (filterset: Filterset) => void;
    /**
     * Build the URL string that `applySavedFilterset` would navigate to —
     * same page, with the filterset's filter expression serialised as URL
     * params and `q.user_filter=:id` appended. Used by `<SavedFilters>` to
     * render each saved-filterset row as `<a href>` so Cmd/Ctrl/middle-
     * click opens the filterset in a new tab (legacy DRT parity), without
     * mutating the current page's URL.
     */
    getFiltersetUrl: (filterset: Filterset) => string;
    deleteFilterset: (id: number) => Promise<void>;
    savedFilters: Filterset[] | undefined;
    isLoadingSavedFilters: boolean;
    activeSavedFilterId: number | null;

    // URL sync
    searchParams: URLSearchParams;
    setSearchParams: (params: Record<string, string>) => void;

    // Utility
    currentFilterExpression: string;
    hasActiveFilters: boolean;
    /** True when pending filters differ from applied filters (ignoring internal _-prefixed keys). */
    isDirty: boolean;
}

const URL_STATE_KEYS = ['page', 'pageSize', 'page_size', 'sort_field', 'sort_dir'] as const;
const SAVED_FILTERS_FETCH_LIMIT = 1000;

/** Strip internal keys (prefixed with `_`) so they never affect dirty-state or active-filter checks. */
const stripInternalKeys = <T extends Record<string, any>>(obj: T): Partial<T> => {
    const result: Partial<T> = {};
    for (const key of Object.keys(obj)) {
        if (!key.startsWith('_')) {
            (result as any)[key] = obj[key];
        }
    }
    return result;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};

const stripUndefinedValues = <T>(value: T): T => {
    if (Array.isArray(value)) {
        return value.map((item) => stripUndefinedValues(item)) as T;
    }

    if (!isPlainObject(value)) {
        return value;
    }

    const result: Record<string, unknown> = {};

    Object.entries(value).forEach(([key, nestedValue]) => {
        if (nestedValue !== undefined) {
            result[key] = stripUndefinedValues(nestedValue);
        }
    });

    return result as T;
};

const areFiltersEqual = (left: unknown, right: unknown): boolean => {
    if (Object.is(left, right)) {
        return true;
    }

    if (Array.isArray(left) && Array.isArray(right)) {
        if (left.length !== right.length) {
            return false;
        }

        return left.every((value, index) => areFiltersEqual(value, right[index]));
    }

    if (isPlainObject(left) && isPlainObject(right)) {
        const leftKeys = Object.keys(left);
        const rightKeys = Object.keys(right);

        if (leftKeys.length !== rightKeys.length) {
            return false;
        }

        return leftKeys.every((key) => rightKeys.includes(key) && areFiltersEqual(left[key], right[key]));
    }

    return false;
};

const getCanonicalSearchParamsString = (params: URLSearchParams): string => {
    return [...params.entries()]
        .sort(([leftKey, leftValue], [rightKey, rightValue]) => {
            if (leftKey === rightKey) {
                return leftValue.localeCompare(rightValue);
            }

            return leftKey.localeCompare(rightKey);
        })
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
};

const defaultParamsToFilter = <T extends Record<string, any>>(params: URLSearchParams): Partial<T> => {
    const parsedFilters = parseFilterExpression(params.toString()) as Partial<T> & { user_filter?: string };
    delete parsedFilters.user_filter;
    return parsedFilters;
};

/**
 * After merging URL/saved-filter params into defaultFilters, coerce field values to
 * match the type implied by the default value:
 *
 * 1. Array fields (default is [] or null): if the parsed value is a plain string,
 *    wrap it in an array. Handles single-value URL params that parseFilterExpression
 *    returns as a string instead of string[].
 *
 * 2. String fields (default is a string): if the parsed value is an array (can happen
 *    when a URL param has multiple values joined with ~), join it back to a string.
 *    Prevents crashes like `filters.user.split is not a function`.
 *
 * This is the single centralized place that guarantees field types always match
 * what the rest of the codebase expects.
 */
const normalizeFilterArrayFields = <T extends Record<string, any>>(merged: T, defaultFilters: T): T => {
    const result = { ...merged };
    for (const key of Object.keys(defaultFilters)) {
        const defaultVal = defaultFilters[key];
        const mergedVal = result[key];
        // Coerce to array: field expects array (default is [] or null) but got a plain string
        if (!Array.isArray(mergedVal) && mergedVal && (Array.isArray(defaultVal) || defaultVal === null)) {
            (result as any)[key] = [mergedVal];
        }
        // Coerce to string: field expects string (default is a string) but got an array
        else if (Array.isArray(mergedVal) && typeof defaultVal === 'string') {
            (result as any)[key] = mergedVal.join(',');
        }
    }
    return result;
};

/**
 * Reusable hook for managing filters with save/load/apply functionality
 *
 * @example
 * ```tsx
 * const {
 *   filters,
 *   setFilter,
 *   applyFilters,
 *   resetFilters,
 *   saveFilterset,
 *   applySavedFilterset,
 *   savedFilters
 * } = useFilterManagement({
 *   targetModel: 'selfservice_user',
 *   defaultFilters: { status: 'All', clientStatus: 'All' }
 * });
 * ```
 */
export const useFilterManagement = <T extends Record<string, any>>({
                                                                       targetModel,
                                                                       defaultFilters,
                                                                       initialDefaultsOnFirstLoad,
                                                                       filterToParams = generateFilterExpression,
                                                                       paramsToFilter = defaultParamsToFilter<T>,
                                                                       filtersToSavedFilterExpression,
                                                                       savedFilterExpressionToFilters,
                                                                   }: FilterManagementConfig<T>): FilterManagementReturn<T> => {
    const [searchParams, setSearchParamsInternal] = useSearchParams();
    const searchParamsString = searchParams.toString();
    const queryClient = useQueryClient();
    const apiClient = useApiClient();
    const filtersetService = useMemo(() => createFiltersetService(apiClient), [apiClient]);
    const { user: currentUser } = useAuth();
    const resolvedTargetModel = useMemo(() => resolveFilterTargetModel(targetModel), [targetModel]);
    const sanitizedSearchParams = useMemo(
        () => sanitizeFilterQueryParams(searchParams),
        [searchParamsString]
    );
    const sanitizedSearchParamsString = useMemo(
        () => sanitizedSearchParams.toString(),
        [sanitizedSearchParams]
    );

    const normalizeManagedFilters = useCallback((sourceFilters: T): T => {
        const canonicalSourceFilters = stripUndefinedValues(sourceFilters);
        const mergedFilters = normalizeFilterArrayFields(
            { ...defaultFilters, ...canonicalSourceFilters } as T,
            defaultFilters
        );
        const serializedFilters = sanitizeUrlFilterExpression(filterToParams(mergedFilters));
        const roundTrippedFilters = paramsToFilter(new URLSearchParams(serializedFilters));

        return normalizeFilterArrayFields(
            { ...mergedFilters, ...roundTrippedFilters } as T,
            defaultFilters
        );
    }, [defaultFilters, filterToParams, paramsToFilter]);

    // Load saved filters for the given target model
    const { data: savedFilters, isLoading: isLoadingSavedFilters } = useQuery<Filterset[], Error>({
        queryKey: ['filtersets', { targetModel: resolvedTargetModel }],
        queryFn: async () => {
            const query = [
                ['equal', 'target_model', resolvedTargetModel],
                ['equal', 'enabled', true],
            ];
            logger.debug('Fetching filters for target_model:', resolvedTargetModel);
            const result = await filtersetService.getFiltersets({
                query,
                limit: SAVED_FILTERS_FETCH_LIMIT,
            });
            logger.debug('Saved filters fetched', {
                targetModel: resolvedTargetModel,
                count: result.filtersets.length,
                filters: result.filtersets.map((filterset) => ({
                    id: filterset.id,
                    name: filterset.name,
                    target_model: filterset.target_model,
                    expression: filterset.expression,
                })),
            });
            return result.filtersets;
        },
        enabled: !!resolvedTargetModel,
    });

    // Current filter state (not yet applied)
    const [filters, setFiltersState] = useState<T>(() => {
        const urlFilters = paramsToFilter(sanitizedSearchParams);
        return normalizeManagedFilters({ ...defaultFilters, ...urlFilters } as T);
    });

    // Applied filters — always derived from URL, source of truth for data fetching.
    // URL is the single source of truth: back/forward navigation, deep linking,
    // and data refetch all work automatically because query keys depend on this value.
    const appliedFilters = useMemo((): T => {
        const urlFilters = paramsToFilter(sanitizedSearchParams);
        return normalizeManagedFilters({ ...defaultFilters, ...urlFilters } as T);
    }, [sanitizedSearchParamsString, defaultFilters, normalizeManagedFilters, paramsToFilter]);
    const activeSavedFilterId = useMemo(() => {
        const userFilterId = searchParams.get('q.user_filter');
        if (!userFilterId) {
            return null;
        }

        const parsedId = Number(userFilterId);
        return Number.isNaN(parsedId) ? null : parsedId;
    }, [searchParamsString]);

    const buildSavedFilterExpression = useCallback((sourceFilters: T): string => {
        const rawExpression = filtersToSavedFilterExpression
            ? filtersToSavedFilterExpression(sourceFilters)
            : filterToParams(sourceFilters);
        const invalidKeys = getInvalidStoredFilterKeys(rawExpression);

        if (invalidKeys.length > 0) {
            logger.warn('Saved filter expression contains non-persisted keys; stripping before storage.', {
                targetModel,
                invalidKeys,
                rawExpression,
            });
        }

        return toStoredFilterExpression(rawExpression);
    }, [filterToParams, filtersToSavedFilterExpression]);

    const parseSavedFilterToFilters = useCallback((expression: string): Partial<T> => {
        const invalidKeys = getInvalidStoredFilterKeys(expression);
        if (invalidKeys.length > 0) {
            logger.warn('Saved filter expression contains malformed or non-filter keys; sanitizing before apply.', {
                targetModel,
                invalidKeys,
                expression,
            });
        }

        const cleanExpression = sanitizeFilterExpression(expression);
        const params = new URLSearchParams(cleanExpression);

        if (savedFilterExpressionToFilters) {
            return savedFilterExpressionToFilters(params);
        }

        return paramsToFilter(params);
    }, [paramsToFilter, savedFilterExpressionToFilters, targetModel]);

    const preserveUrlState = useCallback((params: URLSearchParams) => {
        URL_STATE_KEYS.forEach((key) => {
            const value = searchParams.get(key);
            if (value) {
                params.set(key, value);
            }
        });
    }, [searchParamsString]);

    const buildUrlParamsForFilters = useCallback((
        sourceFilters: T,
        activeFilterId?: string | number | null,
        extraParams?: Record<string, string>
    ) => {
        const params = new URLSearchParams();
        preserveUrlState(params);
        const normalizedSourceFilters = normalizeManagedFilters(sourceFilters);
        const serializedFilters = sanitizeUrlFilterExpression(filterToParams(normalizedSourceFilters));

        for (const [key, value] of new URLSearchParams(serializedFilters).entries()) {
            params.append(key, value);
        }

        if (activeFilterId !== undefined && activeFilterId !== null && activeFilterId !== '') {
            params.set('q.user_filter', String(activeFilterId));
        }

        if (extraParams) {
            Object.entries(extraParams).forEach(([key, value]) => {
                params.set(key, value);
            });
        }
        return sanitizeQueryParamsForNavigation(params);
    }, [filterToParams, normalizeManagedFilters, preserveUrlState]);

    // Refs to hold the latest paramsToFilter and defaultFilters without making them
    // reactive deps of the sync effect. This prevents infinite loops when callers
    // pass inline objects/functions (e.g. defaultFilters: { status: '' } inline).
    const paramsToFilterRef = useRef(paramsToFilter);
    const defaultFiltersRef = useRef(defaultFilters);
    const initialDefaultsOnFirstLoadRef = useRef(initialDefaultsOnFirstLoad);
    const normalizeManagedFiltersRef = useRef(normalizeManagedFilters);
    paramsToFilterRef.current = paramsToFilter;
    defaultFiltersRef.current = defaultFilters;
    initialDefaultsOnFirstLoadRef.current = initialDefaultsOnFirstLoad;
    normalizeManagedFiltersRef.current = normalizeManagedFilters;

    const updateFiltersStateIfChanged = useCallback((nextFilters: T | ((prev: T) => T)) => {
        setFiltersState((prev) => {
            const resolvedFilters = typeof nextFilters === 'function'
                ? (nextFilters as (prev: T) => T)(prev)
                : nextFilters;
            const normalizedPrevFilters = normalizeManagedFilters(prev);
            const normalizedResolvedFilters = normalizeManagedFilters(resolvedFilters);

            if (areFiltersEqual(normalizedPrevFilters, normalizedResolvedFilters)) {
                return prev;
            }

            return normalizedResolvedFilters;
        });
    }, [normalizeManagedFilters]);

    const setSearchParamsIfChanged = useCallback((nextParams: URLSearchParams | Record<string, string>) => {
        const normalizedNextParams = sanitizeQueryParamsForNavigation(nextParams instanceof URLSearchParams
            ? nextParams
            : new URLSearchParams(nextParams));

        if (
            getCanonicalSearchParamsString(searchParams) ===
            getCanonicalSearchParamsString(normalizedNextParams)
        ) {
            return;
        }

        setSearchParamsInternal(normalizedNextParams);
    }, [searchParamsString, setSearchParamsInternal]);

    // Skip the sync effect on the very first render: filters is already initialised
    // from the URL via useState(initialFilters), so firing the effect on mount would
    // just create a redundant state update with identical values.
    const didMountRef = useRef(false);
    const isFirstLoadRef = useRef(true);

    useEffect(() => {
        if (!isFirstLoadRef.current) {
            return;
        }

        const sanitizedParams = sanitizeQueryParamsForNavigation(sanitizedSearchParams);
        const hasQueryParams = hasFilterQueryParams(sanitizedParams);

        if (!hasQueryParams) {
            const firstLoadFilters = normalizeManagedFiltersRef.current({
                ...defaultFiltersRef.current,
                ...(initialDefaultsOnFirstLoadRef.current ?? {}),
            } as T);
            updateFiltersStateIfChanged(firstLoadFilters);
            const defaultParams = buildUrlParamsForFilters(firstLoadFilters);
            setSearchParamsIfChanged(defaultParams);
        } else {
            const urlFilters = paramsToFilterRef.current(sanitizedParams);
            const firstLoadFilters = normalizeManagedFiltersRef.current({
                ...defaultFiltersRef.current,
                ...(initialDefaultsOnFirstLoadRef.current ?? {}),
                ...urlFilters,
            } as T);
            updateFiltersStateIfChanged(firstLoadFilters);
            setSearchParamsIfChanged(buildUrlParamsForFilters(firstLoadFilters));
        }

        isFirstLoadRef.current = false;
    }, [
        buildUrlParamsForFilters,
        sanitizedSearchParamsString,
        setSearchParamsIfChanged,
        updateFiltersStateIfChanged,
    ]);

    // Sync draft filters (UI state) when the URL changes externally — i.e. browser
    // back/forward navigation. Without this, the filter panel would show stale
    // selections after the user navigates back even though appliedFilters (URL-derived)
    // is already correct.
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        const urlFilters = paramsToFilterRef.current(sanitizedSearchParams);
        const nextFilters = normalizeManagedFiltersRef.current({ ...defaultFiltersRef.current, ...urlFilters } as T);

        setFiltersState((prev) => {
            const normalizedPrevFilters = normalizeManagedFiltersRef.current(prev);
            return areFiltersEqual(normalizedPrevFilters, nextFilters) ? prev : nextFilters;
        });
        // searchParamsString is the only reactive dep: URL-driven rehydration should
        // not fire on local draft state changes caused by re-renders.
    }, [sanitizedSearchParamsString]);

    // Update a single filter
    const setFilter = useCallback((key: keyof T, value: any) => {
        updateFiltersStateIfChanged((prev) => ({ ...prev, [key]: value }));
    }, [updateFiltersStateIfChanged]);

    // Update multiple filters at once
    const setFilters = useCallback((newFilters: Partial<T>) => {
        updateFiltersStateIfChanged((prev) => ({ ...prev, ...newFilters }));
    }, [updateFiltersStateIfChanged]);

    // Reset filters to defaults
    const resetFilters = useCallback(() => {
        updateFiltersStateIfChanged(normalizeManagedFilters(defaultFilters));
        const params = new URLSearchParams();
        preserveUrlState(params);
        setSearchParamsIfChanged(params);
    }, [defaultFilters, normalizeManagedFilters, preserveUrlState, setSearchParamsIfChanged, updateFiltersStateIfChanged]);

    // Apply filters (sync to URL)
    const applyFilters = useCallback((extraParams?: Record<string, string>) => {
        const userFilterId = searchParams.get('q.user_filter');
        const params = buildUrlParamsForFilters(filters, userFilterId, extraParams);
        setSearchParamsIfChanged(params);
    }, [buildUrlParamsForFilters, filters, searchParams, setSearchParamsIfChanged]);

    // Save current filters as a filterset
    const saveFilterset = useCallback(async (name: string, isShared: boolean) => {
        try {
            // Client-side duplicate name check against already-loaded savedFilters cache.
            // The proxy authorization layer restricts per-record API queries to empty results,
            // so we rely on the cache (which is correctly fetched for the current session).
            const existsInCache = (savedFilters ?? []).some(
                (f) => f.name.trim().toLowerCase() === name.trim().toLowerCase()
            );
            if (existsInCache) {
                throw new FiltersetNameExistsError();
            }

            // Stored expressions follow the same q.* contract as the URL.
            const normalizedFilters = normalizeManagedFilters(filters);
            const expression = buildSavedFilterExpression(normalizedFilters);
            const createPayload: Partial<Filterset> & { user?: { id: number } } = {
                name,
                target_model: resolvedTargetModel,
                enabled: true,
                shared: isShared,
                expression,
            };
            if (currentUser?.id) {
                createPayload.user = { id: Number(currentUser.id) } as Filterset['user'];
            }
            const createResponse = await filtersetService.createFilterset(createPayload as Partial<Filterset>);
            let createdFilterId: number | null = null;

            const responseData = createResponse?.data as
                | { id?: number; data?: { id?: number } | Array<{ id?: number }> }
                | undefined;

            const directId = Number(responseData?.id);
            if (!Number.isNaN(directId) && directId > 0) {
                createdFilterId = directId;
            }

            if (createdFilterId === null && responseData?.data && !Array.isArray(responseData.data)) {
                const nestedId = Number(responseData.data.id);
                if (!Number.isNaN(nestedId) && nestedId > 0) {
                    createdFilterId = nestedId;
                }
            }

            if (createdFilterId === null && Array.isArray(responseData?.data) && responseData.data.length > 0) {
                const listId = Number(responseData.data[0]?.id);
                if (!Number.isNaN(listId) && listId > 0) {
                    createdFilterId = listId;
                }
            }

            // Invalidate and refetch the saved filters list
            await queryClient.invalidateQueries({
                queryKey: ['filtersets', { targetModel: resolvedTargetModel }]
            });

            // Fallback: infer newly created filter id from refreshed cache
            if (createdFilterId === null) {
                await queryClient.refetchQueries({
                    queryKey: ['filtersets', { targetModel: resolvedTargetModel }],
                    type: 'active',
                });

                const refreshedFilters = queryClient.getQueryData<Filterset[]>(['filtersets', { targetModel: resolvedTargetModel }]) ?? [];
                const matchedFilter = refreshedFilters
                    .filter((filterset) => filterset.name === name && filterset.expression === expression)
                    .sort((a, b) => b.id - a.id)[0];

                if (matchedFilter?.id) {
                    createdFilterId = matchedFilter.id;
                }
            }

            // Select newly created filter
            if (createdFilterId !== null) {
                const params = buildUrlParamsForFilters(normalizedFilters, createdFilterId);
                setSearchParamsIfChanged(params);
            }
        } catch (error) {
            logger.error('Failed to save filterset:', error);
            throw error;
        }
    }, [filters, filtersetService, resolvedTargetModel, queryClient, currentUser, buildSavedFilterExpression, buildUrlParamsForFilters, normalizeManagedFilters, setSearchParamsIfChanged]);

    const searchSavedFilterNames = useCallback(async (searchTerm: string): Promise<Filterset[]> => {
        const trimmedSearchTerm = searchTerm.trim();

        if (!resolvedTargetModel || trimmedSearchTerm.length === 0) {
            return [];
        }

        return queryClient.fetchQuery({
            queryKey: ['filtersets', 'search', { targetModel: resolvedTargetModel, searchTerm: trimmedSearchTerm }],
            queryFn: () =>
                filtersetService.searchFiltersetsByName({
                    targetModel: resolvedTargetModel,
                    searchTerm: trimmedSearchTerm,
                }),
            staleTime: 30_000,
        });
    }, [filtersetService, queryClient, resolvedTargetModel]);

    const updateFilterset = useCallback(async (id: number, data: Partial<Filterset>) => {
        try {
            const updatePayload: Record<string, any> = {};

            updatePayload.id = id;

            if (data.name !== undefined) {
                // Client-side duplicate name check against already-loaded savedFilters cache,
                // but only when the name is actually changing (exclude the record being edited).
                const trimmedName = data.name.trim();
                const existsInCache = (savedFilters ?? []).some(
                    (f) => f.id !== id && f.name.trim().toLowerCase() === trimmedName.toLowerCase()
                );
                if (existsInCache) {
                    throw new FiltersetNameExistsError();
                }
                updatePayload.name = data.name;
            }
            if (data.expression !== undefined) {
                // Normalize editor input back through the page filter contract before
                // rebuilding the canonical stored q.* expression.
                const parsedFilters = paramsToFilter(new URLSearchParams(sanitizeFilterExpression(data.expression)));
                const normalizedFilters = normalizeManagedFilters({ ...defaultFilters, ...parsedFilters } as T);
                updatePayload.expression = buildSavedFilterExpression(normalizedFilters);
            }
            if (data.enabled !== undefined) {
                updatePayload.enabled = data.enabled;
            }
            if (data.shared !== undefined) {
                updatePayload.shared = data.shared;
            }
            if (data.user?.id !== undefined) {
                updatePayload.user = { id: data.user.id };
            }

            await filtersetService.updateFilterset(id, updatePayload as Partial<Filterset>);

            await queryClient.invalidateQueries({
                queryKey: ['filtersets', { targetModel: resolvedTargetModel }],
            });
        } catch (error) {
            logger.error('Failed to update filterset:', error);
            throw error;
        }
    }, [filtersetService, resolvedTargetModel, queryClient, paramsToFilter, defaultFilters, buildSavedFilterExpression, normalizeManagedFilters]);

    // Apply a saved filterset
    const applySavedFilterset = useCallback((filterset: Filterset) => {
        // Sanitize at runtime: guards against legacy DB records that may contain
        // pagination/sort/self-reference keys from before this fix was deployed.
        const parsedFilters = parseSavedFilterToFilters(filterset.expression);

        const newFilters = normalizeManagedFilters({ ...defaultFilters, ...parsedFilters } as T);
        updateFiltersStateIfChanged(newFilters);
        const params = buildUrlParamsForFilters(newFilters, filterset.id);
        setSearchParamsIfChanged(params);
    }, [buildUrlParamsForFilters, defaultFilters, normalizeManagedFilters, parseSavedFilterToFilters, setSearchParamsIfChanged, updateFiltersStateIfChanged]);

    // Pure URL-builder counterpart of applySavedFilterset — returns the URL
    // string that the same-page navigation would produce, without mutating
    // any state. Used by <SavedFilters> to render each row as <a href>.
    const getFiltersetUrl = useCallback((filterset: Filterset): string => {
        const parsedFilters = parseSavedFilterToFilters(filterset.expression);
        const newFilters = normalizeManagedFilters({ ...defaultFilters, ...parsedFilters } as T);
        const params = buildUrlParamsForFilters(newFilters, filterset.id);
        const search = params.toString();
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        return search ? `${path}?${search}` : path;
    }, [buildUrlParamsForFilters, defaultFilters, normalizeManagedFilters, parseSavedFilterToFilters]);

    const deleteFilterset = useCallback(async (id: number) => {
        try {
            await filtersetService.deleteFilterset(id);
            await queryClient.invalidateQueries({
                queryKey: ['filtersets', { targetModel: resolvedTargetModel }],
            });
        } catch (error) {
            logger.error('Failed to delete filterset:', error);
            throw error;
        }
    }, [filtersetService, resolvedTargetModel, queryClient]);

    // Custom setSearchParams wrapper
    const setSearchParams = useCallback((params: Record<string, string>) => {
        setSearchParamsIfChanged(params);
    }, [setSearchParamsIfChanged]);

    // Generate current filter expression
    const currentFilterExpression = useMemo(() => {
        return filterToParams(appliedFilters);
    }, [appliedFilters, filterToParams]);

    // Check if any filters are active (different from defaults)
    const hasActiveFilters = useMemo(() => {
        return Object.keys(filters).some(key => {
            if (key.startsWith('_')) return false;
            const filterValue = filters[key];
            const defaultValue = defaultFilters[key];

            if (Array.isArray(filterValue)) {
                return filterValue.length > 0;
            }

            return filterValue !== defaultValue && filterValue !== '' && filterValue !== null && filterValue !== undefined;
        });
    }, [filters, defaultFilters]);

    // True when pending (draft) filters differ from applied (URL-derived) filters.
    const isDirty = useMemo(() => {
        return !areFiltersEqual(stripInternalKeys(filters), stripInternalKeys(appliedFilters));
    }, [filters, appliedFilters]);

    return {
        filters,
        appliedFilters,
        setFilter,
        setFilters,
        resetFilters,
        applyFilters,
        saveFilterset,
        updateFilterset,
        applySavedFilterset,
        getFiltersetUrl,
        deleteFilterset,
        savedFilters,
        isLoadingSavedFilters,
        activeSavedFilterId,
        searchParams,
        setSearchParams,
        currentFilterExpression,
        hasActiveFilters,
        isDirty,
        searchSavedFilterNames,
    };
};
